import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PDFUploadDropzone } from './PDFUploadDropzone';
import { useUploadPDF } from '../../hooks/usePDFApi';
import { useToast } from '@/shared/hooks/use-toast';
import { PDFFile, previewUploadPDFMetadata } from '../../api';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2 } from 'lucide-react';

interface PDFUploadManagerProps {
  projectId: number;
  initialFile?: File;
  onReset?: () => void;
  onUploadSuccess?: (
    pdfFile: PDFFile,
    sourceFile: File,
    reviewData: { drawingNumber: string; title: string; discipline: string; pageCount: number }
  ) => void;
  onUploadError?: (error: Error) => void;
}

type UploadStep = 'pick' | 'analyzing' | 'review' | 'submitting';

const DISCIPLINE_OPTIONS = [
  'General',
  'Architectural',
  'Interior',
  'Structural',
  'Civil',
  'Landscape',
  'Irrigation',
  'Mechanical',
  'Plumbing',
  'Electrical',
  'Fire Alarm',
  'Fire Protection',
  'Low Voltage',
  'Telecommunications',
  'Radio Enhancement',
  'CCTV',
  'Cameras',
  'Food Service',
  'Aquatics',
  'Medical Millwork',
  'Medical Equipment',
  'Punch List',
  'Quality Control',
  'Radon / Vapor',
  'VMS',
  'Unit Plans',
  'Exterior Building Maintenance',
];


/**
 * PDF Upload Manager Component
 * 
 * Handles the complete upload flow:
 * 1. User selects/drops PDF file
 * 2. Uploads to backend
 * 3. Shows progress/success/error states
 * 4. Calls callback on success
 */
export const PDFUploadManager: React.FC<PDFUploadManagerProps> = ({
  projectId,
  initialFile,
  onReset,
  onUploadSuccess,
  onUploadError,
}) => {
  const { toast } = useToast();
  const uploadMutation = useUploadPDF();
  const [selectedFile, setSelectedFile] = useState<File | null>(initialFile ?? null);
  const [step, setStep] = useState<UploadStep>(initialFile ? 'analyzing' : 'pick');
  const [pageCount, setPageCount] = useState<number>(0);
  const [drawingNumber, setDrawingNumber] = useState('');
  const [title, setTitle] = useState('');
  const [discipline, setDiscipline] = useState('');
  const startedInitialFile = useRef<File | null>(null);
  const isBusy = step === 'analyzing' || step === 'submitting';

  const disciplineOptions = useMemo(
    () => DISCIPLINE_OPTIONS.map((label) => ({ value: label, label })),
    []
  );

  const resetWizard = () => {
    setSelectedFile(null);
    setStep('pick');
    setPageCount(0);
    setDrawingNumber('');
    setTitle('');
    setDiscipline('');
  };

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    setStep('analyzing');

    try {
      const preview = await previewUploadPDFMetadata(file, projectId);
      setDrawingNumber(preview.drawing_number || '');
      setTitle(preview.title || file.name.replace(/\.pdf$/i, '').slice(0, 255));
      setDiscipline(preview.discipline || '');
      setPageCount(preview.page_count || 0);
      setStep('review');
    } catch (error) {
      console.warn('Server pre-upload analysis failed, falling back to filename defaults.', error);
      const fallbackTitle = file.name.replace(/\.pdf$/i, '');
      setDrawingNumber('');
      setTitle(fallbackTitle.slice(0, 255));
      setDiscipline('');
      setPageCount(0);
      setStep('review');
      toast({
        title: 'Analysis incomplete',
        description: 'Could not run OCR+LLM preview. Please fill in the fields manually.',
        variant: 'default',
      });
    }
  }, [projectId, toast]);

  useEffect(() => {
    if (!initialFile || startedInitialFile.current === initialFile) return;
    startedInitialFile.current = initialFile;
    void handleFileSelect(initialFile);
  }, [handleFileSelect, initialFile]);

  const handleReset = () => {
    resetWizard();
    onReset?.();
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setStep('submitting');
    try {
      const pdfFile = await uploadMutation.mutateAsync({
        file: selectedFile,
        projectId,
      });
      toast({
        title: 'Success',
        description: `"${selectedFile.name}" uploaded successfully`,
        variant: 'default',
        className: 'bg-green-500 text-white',
      });
      onUploadSuccess?.(pdfFile, selectedFile, {
        drawingNumber: drawingNumber.trim(),
        title: title.trim(),
        discipline: discipline.trim(),
        pageCount,
      });
      resetWizard();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Failed to upload PDF',
        variant: 'destructive',
      });
      onUploadError?.(error instanceof Error ? error : new Error('Upload failed'));
      setStep('review');
    }
  };

  return (
    <div className="w-full">
      {step === 'pick' && (
        <PDFUploadDropzone
          onFileSelect={handleFileSelect}
          isLoading={false}
          title="Upload Drawing PDF"
          description="Drop a PDF to start OCR prefill, then review before submit."
          buttonLabel="Choose PDF"
          minHeightClassName="min-h-[320px]"
        />
      )}

      {step === 'analyzing' && selectedFile && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Reading PDF and extracting OCR prefill...</p>
                <p className="text-xs text-muted-foreground">{selectedFile.name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'review' && selectedFile && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Review OCR Prefill Before Upload</CardTitle>
            <p className="text-xs text-muted-foreground">
              {selectedFile.name}
              {pageCount > 0 ? ` • ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}` : ''}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Drawing Number</Label>
              <Input
                value={drawingNumber}
                onChange={(e) => setDrawingNumber(e.target.value)}
                className="h-9"
                placeholder="e.g., A4, MEP-101"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-9"
                placeholder="Sheet title"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Discipline</Label>
              <Select value={discipline || '__none__'} onValueChange={(value) => setDiscipline(value === '__none__' ? '' : value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select discipline (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unspecified</SelectItem>
                  {disciplineOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" onClick={handleReset} disabled={isBusy}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isBusy || !selectedFile}>
                {uploadMutation.isPending ? 'Uploading...' : 'Submit Upload'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PDFUploadManager;
